import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

/**
 * The photograph, beside the name rather than as a band above it.
 *
 * A charger is photographed square or upright - it is a tall box on a wall - so a full-width strip
 * either stretched it or cropped the top and bottom off. A fixed portrait tile with `contain` fits
 * both shapes without touching either: what does not fill the tile is quiet grey around it.
 */
export const photoSx: SxProps<Theme> = {
    width: '132px',
    height: '160px',
    flexShrink: 0,
    borderRadius: '10px',
    border: `1px solid ${theme.palette.neutral[200]}`,
    overflow: 'hidden',
    background: theme.palette.neutral[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        display: 'block',
    },
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2.25,
    flexWrap: 'wrap',
    px: 3,
    py: 2.5,
};

export const badgeSx: SxProps<Theme> = {
    width: '52px',
    height: '52px',
    borderRadius: '13px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '28px'},
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
};

/**
 * Vendor, model and OCPP id under the name.
 *
 * The page heading used to read "Name (Model ChargePointId)" - three identifiers of different kinds
 * in one line, with the machine-readable one in brackets at the end.
 */
export const subtitleSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.4,
    '& b': {fontVariantNumeric: 'tabular-nums', fontWeight: 500},
};

export const factsSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)'},
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};

/**
 * A cell that can end in an action.
 *
 * A column, so the button sits at its foot however tall the value above it is - which is what makes
 * the two actions in this row line up with each other instead of floating at whatever height their
 * own text happens to end.
 */
export const factSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    px: 2.75,
    pt: 2.5,
    pb: 0,
    borderRight: `1px solid ${theme.palette.neutral[100]}`,
    '&:last-of-type': {borderRight: 'none'},
};

export const factLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const factValueSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
    mt: 0.4,
    mb: 2.5,
};

export const factEmptySx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 400,
    fontStyle: 'italic',
    color: theme.palette.neutral[400],
    mt: 0.4,
    mb: 2.5,
};

export const factNoteSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    mt: 0.3,
};

/**
 * The cell's action, as its footer.
 *
 * Full width at the bottom of the cell, the way a table's add-row sits at the foot of its card: the
 * action belongs to everything above it, and a small button floating after a value reads as part of
 * the value.
 */
export const factFooterButtonSx = (tone?: 'danger'): SxProps<Theme> => ({
    mt: 'auto',
    mx: -2.75,
    width: 'auto',
    justifyContent: 'center',
    height: '38px',
    borderRadius: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    color: tone === 'danger' ? theme.palette.brand.dark : theme.palette.primary.dark,
    background: '#FAFBFD',
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    '& .MuiButton-startIcon .MuiSvgIcon-root': {fontSize: '1rem'},
    '&:hover': {
        background: tone === 'danger' ? '#FEF3F6' : theme.palette.primaryColor.A50,
    },
});

/** Unlinking a client is destructive but small: an outline in the danger colour, not a filled button. */
export const unlinkButtonSx: SxProps<Theme> = {
    height: '28px',
    minHeight: '28px',
    px: 1.25,
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    color: theme.palette.brand.dark,
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.brand.light}`,
    '& .MuiButton-startIcon .MuiSvgIcon-root': {fontSize: '1rem'},
    '&:hover': {
        boxShadow: 'none',
        background: '#FEF3F6',
        border: `1px solid ${theme.palette.brand.main}`,
    },
};

/**
 * The device card.
 *
 * Reset, configurations, logs, the QR code and the cable lock were a tail of the details form,
 * separated from it by two dividers - so the things you do TO the hardware sat inside the record of
 * what the hardware IS.
 */
export const deviceHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 3,
    py: 2,
};

export const deviceBadgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '18px'},
};

export const deviceTitleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

/** The lock sits opposite the title: it is the device's state, not a sixth action. */
export const deviceLockSx: SxProps<Theme> = {
    ml: 'auto',
};

/**
 * The strip of actions, edge to edge.
 *
 * `auto-fit` with a floor rather than a fixed five: on the page's full width all five sit side by
 * side and share it equally; in the narrow rail they fall to two rows of equal cells instead of a
 * ragged wrap. Nothing is ever squeezed below the width its longest translation needs.
 */
export const deviceBodySx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
};


